import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Button, DatePicker, Empty, Image, Spin } from 'antd'
import {
  ArrowLeftOutlined,
  CameraOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons'

import { useAuth } from '../hooks/AuthContext'
import useGoBack from '../hooks/useGoBack'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { listServicePhotosByCustomer } from '../api/servicePhoto'
import { toServicePhotoUrl } from '../components/ServicePhotoThumbs'
import ResponsiveSelect from '../components/common/ResponsiveSelect'
import Hint from '../components/common/Hint'
import { fromPicker } from '../utils/timezone'

/**
 * 顧客的「施作照片紀錄」完整頁：/customers/:customerId/service-photos
 *
 * 顧客詳情只放最近 3 次的預覽，這裡才是完整的時間軸：
 *   - 依月份分段，月份標題捲動時固定在上方
 *   - 每次施作完整顯示日期、服務、結帳備註與全部照片
 *   - 一次載入 10 次，捲到底自動載入下一批
 *   - 可依服務項目、消費日期區間篩選
 *   - 放大後可一路左右翻（同一個 PreviewGroup 包住整頁已載入的照片）
 *
 * 日期是結帳單上的牆上日期字串（YYYY-MM-DD），直接比對與顯示，不做時區換算；
 * 只有日期選擇器挑出來的值要先過 fromPicker()（見 CLAUDE.md 時區規則）。
 */

const PAGE_SIZE = 10

const Page = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 8px 4px 40px;
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const TitleBlock = styled.div`
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1d1d1f;
    display: flex;
    align-items: center;
    gap: 8px;

    .anticon {
      color: #d5728a;
    }
  }

  .sub {
    margin-top: 2px;
    font-size: 13px;
    color: #86868b;
  }
`

const Filters = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 220px) minmax(0, 300px) auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;

  .count {
    font-size: 13px;
    color: #86868b;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
`

/* 月份標題：捲動時貼在上方。手機版上方有 55px 的固定導覽列 */
const MonthHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 8px 2px;
  margin: 8px 0 4px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(6px);
  font-size: 14px;
  font-weight: 700;
  color: #8a4a5c;
  border-bottom: 1px solid #f5dfe7;

  @media (max-width: 820px) {
    top: 55px;
  }
`

const Visit = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafb;
  border: 1px solid #f0f0f3;
  margin-top: 10px;
`

const VisitHead = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;

  .date {
    font-size: 15px;
    font-weight: 700;
    color: #0f2562;
    white-space: nowrap;
  }
  .services {
    font-size: 14px;
    color: #4b5563;
    overflow-wrap: anywhere;
  }
  .order {
    margin-left: auto;
    font-size: 12px;
    color: #b0b0b5;
  }
`

const Tag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #86868b;
  background: #f0f0f3;
  border-radius: 10px;
  padding: 1px 8px;
`

const Remarks = styled.div`
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.7;
  color: #374151;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .label {
    color: #9ca3af;
    margin-right: 6px;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  margin-top: 10px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
  }
`

const Tile = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  background: #f4f5f7;

  .ant-image,
  .ant-image-img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    display: block;
    cursor: zoom-in;
  }
`

const HiddenBadge = styled.span`
  position: absolute;
  right: 4px;
  bottom: 4px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  pointer-events: none;
`

const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0 8px;
  color: #b0b0b5;
  font-size: 13px;
`

const monthLabel = (date) => {
  if (!date || date.length < 7) return '日期不明'
  return `${date.slice(0, 4)} 年 ${Number(date.slice(5, 7))} 月`
}

const CustomerServicePhotosPage = () => {
  const { customerId } = useParams()
  const { businessId } = useAuth()
  const goBack = useGoBack(`/customers/${customerId}`)
  const { hasFeature, permissionsLoaded } = useMembership()
  const { canAny } = usePermission()
  const allowed =
    hasFeature('servicePhoto') &&
    canAny(['servicePhoto:view', 'servicePhoto:manage'])

  const [service, setService] = useState()
  const [dateRange, setDateRange] = useState(null)

  const [groups, setGroups] = useState([])
  const [meta, setMeta] = useState({
    customerName: '',
    summary: { visits: 0, photos: 0 },
    serviceOptions: [],
    totalGroups: 0,
    total: 0
  })
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 篩選改了要丟掉還在路上的舊請求結果，用遞增序號判斷
  const requestSeq = useRef(0)

  const filterParams = useMemo(() => {
    const params = {}
    if (service) params.service = service
    if (dateRange?.[0] && dateRange?.[1]) {
      params.dateStart = fromPicker(dateRange[0]).format('YYYY-MM-DD')
      params.dateEnd = fromPicker(dateRange[1]).format('YYYY-MM-DD')
    }
    return params
  }, [service, dateRange])

  const load = useCallback(
    async (pageNum) => {
      if (!allowed || !businessId || !customerId) return
      const seq = ++requestSeq.current
      setLoading(true)
      setError('')
      try {
        const res = await listServicePhotosByCustomer(businessId, customerId, {
          ...filterParams,
          page: pageNum,
          limit: PAGE_SIZE
        })
        if (seq !== requestSeq.current) return
        const data = res?.data?.data || {}
        const list = Array.isArray(data.groups) ? data.groups : []
        setGroups((prev) => (pageNum === 1 ? list : [...prev, ...list]))
        setPage(pageNum)
        setHasMore(!!data.hasMore)
        setMeta({
          customerName: data.customer?.name || '',
          summary: data.summary || { visits: 0, photos: 0 },
          serviceOptions: data.serviceOptions || [],
          totalGroups: data.totalGroups || 0,
          total: data.total || 0
        })
      } catch (err) {
        if (seq !== requestSeq.current) return
        setError(err?.response?.data?.message || '取得施作照片失敗，請稍後再試')
      } finally {
        if (seq === requestSeq.current) setLoading(false)
      }
    },
    [allowed, businessId, customerId, filterParams]
  )

  // 第一次進來、或篩選條件變了：從第一頁重來
  useEffect(() => {
    load(1)
  }, [load])

  // 捲到底自動載入下一批；觀察器之外也保留「載入更多」按鈕當備援
  const sentinelRef = useRef(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore || loading) return undefined
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) load(page + 1)
      },
      { rootMargin: '400px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, loading, load, page])

  // 依月份分段（groups 已是新到舊）
  const sections = useMemo(() => {
    const out = []
    groups.forEach((g) => {
      const label = monthLabel(g.date)
      const last = out[out.length - 1]
      if (last && last.label === label) last.items.push(g)
      else out.push({ label, items: [g] })
    })
    return out
  }, [groups])

  const isFiltering = !!(service || dateRange)

  if (permissionsLoaded && !allowed) {
    return (
      <Page>
        <TopBar>
          <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
            返回
          </Button>
        </TopBar>
        <Hint tone='info' title='施作照片紀錄'>
          這個功能需要品牌方案，且帳號要有施作照片的檢視權限。
        </Hint>
      </Page>
    )
  }

  return (
    <Page>
      <TopBar>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
          返回
        </Button>
        <TitleBlock>
          <h1>
            <CameraOutlined />
            {meta.customerName ? `${meta.customerName}的施作照片` : '施作照片紀錄'}
          </h1>
          <div className='sub'>
            共 {meta.summary.visits} 次施作・{meta.summary.photos} 張照片
          </div>
        </TitleBlock>
      </TopBar>

      <Filters>
        <ResponsiveSelect
          allowClear
          placeholder='全部服務'
          value={service}
          onChange={(v) => setService(v || undefined)}
          options={meta.serviceOptions.map((name) => ({
            value: name,
            label: name
          }))}
          style={{ width: '100%' }}
        />
        <DatePicker.RangePicker
          value={dateRange}
          onChange={(v) => setDateRange(v && v[0] && v[1] ? v : null)}
          placeholder={['開始日期', '結束日期']}
          format='YYYY-MM-DD'
          allowClear
          style={{ width: '100%' }}
          inputReadOnly
        />
        {isFiltering && (
          <span className='count'>
            符合 {meta.totalGroups} 次・{meta.total} 張
          </span>
        )}
      </Filters>

      {error && (
        <Hint tone='error' title='載入失敗'>
          {error}
        </Hint>
      )}

      {loading && groups.length === 0 ? (
        <Footer>
          <Spin />
        </Footer>
      ) : groups.length === 0 && !error ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={isFiltering ? '沒有符合條件的施作照片' : '還沒有施作照片'}
          style={{ margin: '48px 0' }}
        />
      ) : (
        // 整頁共用一個 PreviewGroup：放大後可以一路往前翻到更早的施作
        <Image.PreviewGroup>
          {sections.map((section) => (
            <div key={section.label}>
              <MonthHeader>{section.label}</MonthHeader>
              {section.items.map((group) => (
                <Visit key={group.bookingId}>
                  <VisitHead>
                    <span className='date'>
                      {group.date || '日期不明'}
                      {group.time ? ` ${group.time}` : ''}
                    </span>
                    {group.services?.length > 0 && (
                      <span className='services'>
                        {group.services.join('、')}
                      </span>
                    )}
                    {!group.checkedOut && <Tag>尚未結帳</Tag>}
                    {group.orderStatus === 'voided' && <Tag>結帳已作廢</Tag>}
                    {group.orderNo && (
                      <span className='order'>#{group.orderNo}</span>
                    )}
                  </VisitHead>
                  {group.remarks ? (
                    <Remarks>
                      <span className='label'>備註</span>
                      {group.remarks}
                    </Remarks>
                  ) : null}
                  <Grid>
                    {group.photos.map((photo) => (
                      <Tile key={photo._id}>
                        <Image
                          src={toServicePhotoUrl(photo.previewUrl || photo.url)}
                          preview={{ src: toServicePhotoUrl(photo.url) }}
                          alt={photo.caption || '施作照片'}
                          loading='lazy'
                        />
                        {photo.visibleToCustomer === false && (
                          <HiddenBadge title='未對客人公開'>
                            <EyeInvisibleOutlined />
                          </HiddenBadge>
                        )}
                      </Tile>
                    ))}
                  </Grid>
                </Visit>
              ))}
            </div>
          ))}
        </Image.PreviewGroup>
      )}

      {groups.length > 0 && (
        <Footer ref={sentinelRef}>
          {hasMore ? (
            <Button loading={loading} onClick={() => load(page + 1)}>
              載入更多
            </Button>
          ) : (
            '已經是最早的施作了'
          )}
        </Footer>
      )}
    </Page>
  )
}

export default CustomerServicePhotosPage
